//Dependencies............................................
import { Tabs, Form, Dropdown, Button, Space, Input, InputNumber, Modal, Collapse, Tooltip, Select, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { ItemType } from 'antd/es/menu/interface';
import { DeleteOutlined, EditOutlined, ExclamationOutlined, PlusOutlined } from '@ant-design/icons';

//Components..............................................
import DeleteModal from "../../../../GlobalComponents/Modals/DeleteModal";

const { Text } = Typography;

const checksumItems: ItemType[] = [
    { label: 'classic', key: 'classic' },
    { label: 'enhanced', key: 'enhanced' },
];

const dataLenOptions = ["1", "2", "3", "4", "5", "6", "7", "8"];

// Matches car-config.schema.json's lin_signals[].bit_length maximum and the
// firmware's uint64_t signal-value accumulator (signal_cfg_hndl.c) - a
// signal can span up to the full 8-byte (64-bit) LIN frame.
const MAX_SIGNAL_BIT_LENGTH = 64;

// Distinct highlight colors for overlaying multiple signals on one frame's
// bit grid - cycles if a frame somehow has more signals than colors.
const SIGNAL_COLORS = ['#1677ff', '#52c41a', '#fa8c16', '#eb2f96', '#722ed1', '#13c2c2', '#a0d911', '#f5222d'];

export default  function Conf30LinMessageConfigField({
    onChange,
    formData,
    errorSchema,
    linSignals,
    onSignalsChange,
    }   :   {
    formData: any,
    onChange: (newFormData: any) => void,
    errorSchema: any,
    linSignals: any[],
    onSignalsChange: (newSignals: any[]) => void,
    })
{
    const [selectedConfig, setSelectedConfig] = useState<{ [key: string]: any }>({});

    const [selectedTabName, setSelectedTabName] = useState<string | null>(null);
    const [newTabName, setNewTabName] = useState<string>('');
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

    const signals: any[] = Array.isArray(linSignals) ? linSignals : [];

    useEffect(() => {
        if (formData) {
            setSelectedConfig(formData);
        }
    }, [formData]);

    const updateConfig = (frameId: string, key: string, value: any) => {
        const updatedConfig = {
            ...selectedConfig,
            [frameId]: {
                ...selectedConfig[frameId],
                [key]: value,
            },
        };

        setSelectedConfig(updatedConfig);
        onChange(updatedConfig);
    };

    const handleAddTab = () => {
        let newFrameId = `0x00`;
        while (selectedConfig[newFrameId]) {
            const randomId = Math.floor(Math.random() * 0x3F);
            newFrameId = `0x${randomId.toString(16).toUpperCase().padStart(2, '0')}`;
        }

        const newConfig = {
            ...selectedConfig,
            [newFrameId]: {
                data_len: null,
                checksum_type: '',
                timeout_ms: null,
            },
        };
        setSelectedConfig(newConfig);
        onChange(newConfig);
    };

    const showRenameModal = (frameId: string) => {
        setSelectedTabName(frameId);
        setNewTabName(frameId);
        setIsEditing(true);
    };

    const handleRenameTab = () => {
        if (selectedTabName && newTabName) {
            if (newTabName !== selectedTabName) {
                const updatedConfig = {
                    ...selectedConfig,
                    [newTabName]: { ...selectedConfig[selectedTabName] },
                };
                delete updatedConfig[selectedTabName];

                setSelectedConfig(updatedConfig);
                onChange(updatedConfig);

                // Signals reference frame_id by value, not by position - keep
                // them pointed at the renamed frame instead of silently
                // becoming invalid.
                onSignalsChange(signals.map(signal =>
                    signal.frame_id === selectedTabName ? { ...signal, frame_id: newTabName } : signal
                ));
            }
        }
        setSelectedTabName(null);
        setNewTabName('');
        setIsEditing(false);
    };

    const signalsForFrame = (frameId: string) => signals
        .map((signal, globalIndex) => ({ signal, globalIndex }))
        .filter(({ signal }) => signal.frame_id === frameId);

    const addSignal = (frameId: string) => {
        onSignalsChange([...signals, { name: 'New Signal', frame_id: frameId, bit_number: 0, bit_length: 1 }]);
    };

    const updateSignalField = (globalIndex: number, key: string, value: any) => {
        onSignalsChange(signals.map((signal, i) => (i === globalIndex ? { ...signal, [key]: value } : signal)));
    };

    const deleteSignal = (globalIndex: number) => {
        onSignalsChange(signals.filter((_, i) => i !== globalIndex));
    };

    const frameBits = (frameId: string) => {
        const dataLen = selectedConfig[frameId]?.data_len;
        return (dataLen ? dataLen : 8) * 8;
    };

    // Moving the start bit is a single-signal edit against a hard limit (the
    // frame's own size), so the length is clamped down right along with it
    // rather than just left dangling past the frame boundary - unlike
    // overlaps with other signals (see getSignalIssues), which are only
    // flagged, since two signals' ranges can legitimately pass through a
    // temporary overlap while the user is still editing them.
    const updateSignalStartBit = (frameId: string, globalIndex: number, newBitNumber: number) => {
        const maxBits = frameBits(frameId);
        const currentLength = signals[globalIndex]?.bit_length ?? 1;
        const maxLengthAtNewStart = Math.max(1, Math.min(MAX_SIGNAL_BIT_LENGTH, maxBits - newBitNumber));
        const newBitLength = Math.min(currentLength, maxLengthAtNewStart);
        onSignalsChange(signals.map((signal, i) =>
            i === globalIndex ? { ...signal, bit_number: newBitNumber, bit_length: newBitLength } : signal
        ));
    };

    const rangesOverlap = (aStart: number, aLength: number, bStart: number, bLength: number) =>
        aStart < bStart + bLength && bStart < aStart + aLength;

    // A signal is invalid if it runs past the frame's own bit length, or if
    // its bit range overlaps another signal on the same frame.
    const getSignalIssues = (frameId: string, globalIndex: number) => {
        const signal = signals[globalIndex];
        const bitNumber = signal?.bit_number ?? 0;
        const bitLength = signal?.bit_length ?? 1;
        const maxBits = frameBits(frameId);

        const outOfBounds = bitNumber + bitLength > maxBits;
        const overlapping = signalsForFrame(frameId)
            .filter(({ globalIndex: otherIndex }) => otherIndex !== globalIndex)
            .filter(({ signal: other }) =>
                rangesOverlap(bitNumber, bitLength, other.bit_number ?? 0, other.bit_length ?? 1)
            )
            .map(({ signal: other }) => other.name || 'Unnamed signal');

        return { outOfBounds, overlapping };
    };

    // Renders the frame's data bytes as a grid of individual bit cells (MSB
    // to LSB within each byte, byte 0 first), overlaying every signal
    // defined on this frame at once, each in its own color. A bit claimed by
    // more than one signal (a real overlap) renders in red instead, since
    // "whichever signal happens to be first" would hide the conflict.
    const renderFrameSignalsBitGrid = (frameId: string, frameSignals: { signal: any, globalIndex: number }[]) => {
        const dataLen = selectedConfig[frameId]?.data_len || 8;

        const ownersForBit = (globalBit: number) => frameSignals.filter(({ signal }) => {
            const bitNumber = signal.bit_number ?? 0;
            const bitLength = signal.bit_length ?? 1;
            return globalBit >= bitNumber && globalBit <= bitNumber + bitLength - 1;
        });

        return (
            <div style={{ marginTop: 12, padding: 16, border: '1px solid #f0f0f0', borderRadius: 8, background: '#fafafa' }}>
                {Array.from({ length: dataLen }, (_, byteIndex) => byteIndex).map((byteIndex) => (
                    <div key={byteIndex} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ width: 64, fontSize: 12, color: '#888', flexShrink: 0 }}>Byte {byteIndex}</span>
                        <Space size={6}>
                            {Array.from({ length: 8 }, (_, i) => 7 - i).map((bitInByte) => {
                                const globalBit = byteIndex * 8 + bitInByte;
                                const owners = ownersForBit(globalBit);
                                const isConflict = owners.length > 1;
                                const color = isConflict
                                    ? '#ff4d4f'
                                    : owners.length > 0
                                        ? SIGNAL_COLORS[frameSignals.indexOf(owners[0]) % SIGNAL_COLORS.length]
                                        : null;
                                const title = isConflict
                                    ? `bit ${globalBit} - overlap: ${owners.map(({ signal }) => signal.name || 'Unnamed signal').join(', ')}`
                                    : `bit ${globalBit}`;
                                return (
                                    <div
                                        key={globalBit}
                                        title={title}
                                        style={{
                                            width: 26,
                                            height: 26,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 10,
                                            borderRadius: 4,
                                            border: `1px solid ${color || '#d9d9d9'}`,
                                            background: color || '#fff',
                                            color: color ? '#fff' : '#999',
                                            fontWeight: color ? 600 : 400,
                                            outline: isConflict ? '2px solid #a8071a' : undefined,
                                        }}
                                    >
                                        {globalBit}
                                    </div>
                                );
                            })}
                        </Space>
                    </div>
                ))}
                {frameSignals.length > 0 && (
                    <Space wrap style={{ marginTop: 8 }}>
                        {frameSignals.map(({ signal }, i) => (
                            <Space key={i} size={4}>
                                <div style={{ width: 10, height: 10, borderRadius: 2, background: SIGNAL_COLORS[i % SIGNAL_COLORS.length] }} />
                                <Text style={{ fontSize: 12 }}>{signal.name || 'Unnamed signal'}</Text>
                            </Space>
                        ))}
                    </Space>
                )}
            </div>
        );
    };

    return (
        <>
            <Collapse>
                <Collapse.Panel header="Lin Message Config" key="1" >
                    <Tabs
                        type="editable-card"
                        onEdit={(key, action) => {
                            if (action === 'add') {
                                handleAddTab();
                            } else if (action === 'remove') {
                                setSelectedTabName(key as string);
                                setShowDeleteModal(true);
                            }
                        }}
                        addIcon="+"
                    >
                        {Object.keys(selectedConfig).map((frameId) =>{
                            const hasErrors = !!(errorSchema as any)?.lin_message_config?.[frameId];
                            const dataLenErrors = !!(errorSchema as any)?.lin_message_config?.[frameId]?.data_len;
                            const timeoutErrors = !!(errorSchema as any)?.lin_message_config?.[frameId]?.timeout_ms;
                            const maxBits = frameBits(frameId);
                            const frameSignals = signalsForFrame(frameId);

                            return (
                                <Tabs.TabPane
                                    tab={
                                        <span>
                                            <>
                                                <Tooltip title="Rename Tab">
                                                        <EditOutlined
                                                            onClick={() => showRenameModal(frameId)}
                                                            style={{ cursor: 'pointer', marginLeft: 5 }}
                                                        />
                                                </Tooltip>
                                                {'  '}{frameId}{'  '}
                                                {hasErrors && (
                                                    <ExclamationOutlined
                                                        style={{ color: 'red', fontSize: 15 }}
                                                    />)
                                                }
                                            </>
                                        </span>
                                    }
                                    key={frameId}
                                    closable
                                >
                                    <Form layout="horizontal">
                                        <Form.Item label="Data Length (bytes)">
                                            <Select
                                                value={selectedConfig[frameId]?.data_len ? String(selectedConfig[frameId]?.data_len) : undefined}
                                                style={{
                                                    width: 120,
                                                    border: dataLenErrors ? '1px solid red' : undefined,
                                                }}
                                                onChange={(value) => {
                                                    updateConfig(frameId, "data_len", parseInt(value, 10));
                                                }}
                                                options={dataLenOptions.map(v => ({
                                                    label: v,
                                                    value: v,
                                                }))}
                                            />
                                        </Form.Item>
                                        <Form.Item label="Checksum">
                                            <Dropdown
                                                menu={{
                                                    items: checksumItems,
                                                    selectable: true,
                                                    onClick: (e) => updateConfig(frameId, 'checksum_type', e.key),
                                                }}
                                            >
                                                <Button>
                                                    <Space>{selectedConfig[frameId]?.checksum_type || 'Select Checksum'}</Space>
                                                </Button>
                                            </Dropdown>
                                        </Form.Item>
                                        <Form.Item label="Timeout (ms)">
                                            <Input
                                                type="number"
                                                value={selectedConfig[frameId]?.timeout_ms}
                                                style={{ width: '10%', border: timeoutErrors ? '1px solid red' : undefined }}
                                                onChange={(e) => updateConfig(frameId, 'timeout_ms', parseInt(e.target.value, 10))}
                                            />
                                        </Form.Item>
                                    </Form>
                                    <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>Signals</Text>
                                    {frameSignals.map(({ signal, globalIndex }) => {
                                        const bitNumber = signal.bit_number ?? 0;
                                        const bitLength = signal.bit_length ?? 1;
                                        const { outOfBounds, overlapping } = getSignalIssues(frameId, globalIndex);
                                        const hasIssue = outOfBounds || overlapping.length > 0;
                                        const issueMessage = outOfBounds
                                            ? `Extends past the frame's ${maxBits}-bit length`
                                            : overlapping.length > 0
                                                ? `Overlaps with: ${overlapping.join(', ')}`
                                                : undefined;
                                        return (
                                            <Space key={globalIndex} style={{ display: 'flex', marginBottom: 8 }} align="start">
                                                <Input
                                                    placeholder="Signal name"
                                                    value={signal.name}
                                                    style={{ width: 220 }}
                                                    onChange={(e) => updateSignalField(globalIndex, 'name', e.target.value)}
                                                />
                                                <Tooltip title={issueMessage}>
                                                    <InputNumber
                                                        addonBefore="Start bit"
                                                        min={0}
                                                        max={Math.max(0, maxBits - 1)}
                                                        value={bitNumber}
                                                        status={hasIssue ? "error" : undefined}
                                                        onChange={(value) => updateSignalStartBit(frameId, globalIndex, value ?? 0)}
                                                    />
                                                </Tooltip>
                                                <Tooltip title={issueMessage}>
                                                    <InputNumber
                                                        addonBefore="Length"
                                                        min={1}
                                                        max={Math.max(1, Math.min(MAX_SIGNAL_BIT_LENGTH, maxBits - bitNumber))}
                                                        value={bitLength}
                                                        status={hasIssue ? "error" : undefined}
                                                        onChange={(value) => updateSignalField(globalIndex, 'bit_length', value ?? 1)}
                                                    />
                                                </Tooltip>
                                                <Button
                                                    icon={<DeleteOutlined />}
                                                    danger
                                                    onClick={() => deleteSignal(globalIndex)}
                                                />
                                            </Space>
                                        );
                                    })}
                                    <Button icon={<PlusOutlined />} onClick={() => addSignal(frameId)}>
                                        Add Signal
                                    </Button>
                                    {renderFrameSignalsBitGrid(frameId, frameSignals)}
                                </Tabs.TabPane>
                            )
                        })}
                    </Tabs>
                </Collapse.Panel>
            </Collapse>
            <Modal
                title="Rename Tab"
                open={isEditing}
                onOk={handleRenameTab}
                onCancel={() => {
                    setSelectedTabName(null);
                    setIsEditing(false);
                }}
            >
                <Input
                    value={newTabName}
                    onChange={(e) => setNewTabName(e.target.value)}
                    placeholder="Enter new tab name"
                />
            </Modal>
            <DeleteModal
                showModal={showDeleteModal}
                onDelete={() => {
                    const newConfig = { ...selectedConfig };
                    delete newConfig[selectedTabName!];
                    setSelectedConfig(newConfig);
                    onChange(newConfig);
                    // Drop signals that belonged only to the removed frame -
                    // otherwise they'd point at a frame_id that no longer
                    // exists.
                    onSignalsChange(signals.filter(signal => signal.frame_id !== selectedTabName));
                    setShowDeleteModal(false);
                    setSelectedTabName(null);
                }}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedTabName(null);
                }}
            />
        </>
    );
}
